import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  MagnifyingGlass,
  CaretRight,
  Certificate,
  Sparkle
} from '@phosphor-icons/react';
import { internships, internshipCategories, internshipCover, internshipCategoryLabel } from '../data/internships';
import { internshipSeo } from '../data/internshipSeo';
import { applyPageSeo, SITE_URL } from '../utils/seo';

const Careers = () => {
  const navigate = useNavigate();
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Paid Internship in Satara | Growmore IT Services',
      itemListElement: internships.map((role, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: role.title,
        url: `${SITE_URL}/internship/${role.slug}`
      }))
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'internship-jsonld';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
    const restore = applyPageSeo({
      title: 'Paid Internship in Satara | Full Stack, Flutter, SEO | Growmore',
      description:
        'Paid internship in Satara for full stack, frontend, backend, Flutter, Android, AI, UI UX, digital marketing and cyber security. Weekly tasks and official letters.',
      keywords:
        'Paid Internship in Satara, Internship in Satara, Full Stack Internship Satara, Flutter Internship Satara, Software Internship Satara, Digital Marketing Internship Satara, AI Internship Satara, Growmore IT Services',
      path: '/internship'
    });
    return () => {
      restore();
      document.getElementById('internship-jsonld')?.remove();
    };
  }, []);

  const filtered = internships.filter((role) => {
    const catOk = category === 'all' || role.category === category;
    const q = query.trim().toLowerCase();
    const extra = internshipSeo[role.slug];
    const qOk = !q || [role.title, role.summary, role.keywords, role.about, extra?.headline, extra?.seoKeywords].join(' ').toLowerCase().includes(q);
    return catOk && qOk;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800">
      <section className="relative pt-24 sm:pt-28 pb-6 sm:pb-8 overflow-hidden">
        <div className="absolute -top-16 right-10 w-64 h-64 bg-brand-400/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-8 w-40 h-40 bg-violet-400/10 rounded-full blur-3xl" />
        <div className="section-max-width px-4 md:px-6 relative">
          <div className="mb-4 text-center sm:text-left">
            <p className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full badge-primary mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] font-sans">
              <GraduationCap className="w-4 h-4" weight="fill" /> Paid internship · Satara
            </p>
            <h1 className="font-sans text-[1.2rem] sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight tracking-tight whitespace-nowrap sm:whitespace-normal">
              Choose a track. <span className="text-gradient">Build real work.</span>
              <span className="sr-only"> - Paid IT Internship in Satara | Growmore IT Services</span>
            </h1>
            <p className="mt-3 font-sans text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-6 sm:leading-7">
              <span className="block">Paid internship in Satara from Growmore IT Services — full stack, Flutter, Android, AI, design, SEO and cyber security tracks.</span>
              <span className="block mt-1">Select a track, complete weekly tasks, and get QR-verified offer and completion letters.</span>
            </p>
            <Link
              to="/certificates"
              className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold px-3.5 py-1.5 font-sans"
            >
              <Certificate className="w-3.5 h-3.5" weight="duotone" />
              Download certificate
            </Link>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="section-max-width px-4 md:px-6">
          <div className="rounded-2xl bg-white/80 dark:bg-dark-800/80 border border-gray-100 dark:border-gray-800 p-3 sm:p-4 mb-6 shadow-soft">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2">
                {internshipCategories.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setCategory(item.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border smooth-transition ${
                      category === item.id
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white dark:bg-dark-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-brand-200'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
              <label className="relative w-full">
                <MagnifyingGlass className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 shrink-0" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-dark-800 py-2.5 pl-11 pr-4 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                  placeholder="Filter roles — Flutter, SEO, Java..."
                />
              </label>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((role, index) => (
              <button
                key={role.slug}
                type="button"
                onClick={() => navigate(`/internship/${role.slug}`)}
                className="intern-card-enter intern-role-card group relative rounded-2xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-800 shadow-soft overflow-hidden text-left hover:-translate-y-2 hover:shadow-2xl hover:border-brand-400 hover:ring-2 hover:ring-brand-100 dark:hover:ring-brand-900/40 smooth-transition"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={internshipCover(role.slug)}
                    alt=""
                    className="intern-card-cover w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-900/25 to-transparent opacity-80 group-hover:opacity-100 smooth-transition" />
                  <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wide bg-white/95 text-brand-700 px-2.5 py-1 rounded-md shadow-sm">
                    {internshipCategoryLabel(role.category)}
                  </span>
                  <span className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white text-brand-700 flex items-center justify-center shadow-lg translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 smooth-transition">
                    <CaretRight className="w-4 h-4" weight="bold" />
                  </span>
                </div>
                <div className="p-4 pb-5">
                  <h2 className="font-sans text-base font-bold text-gray-900 dark:text-white leading-snug group-hover:text-brand-700 smooth-transition">
                    {role.title}
                  </h2>
                  <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1.5 leading-5 line-clamp-2">{role.summary}</p>
                  <div className="flex items-center justify-between gap-2 mt-3.5">
                    <span className="text-[10px] font-semibold bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 px-2 py-0.5 rounded-md inline-flex items-center gap-1">
                      <Sparkle className="w-3 h-3" /> Open to all
                    </span>
                    <span className="text-[11px] font-semibold text-brand-600 inline-flex items-center gap-0.5 group-hover:gap-1.5 smooth-transition">
                      View track <CaretRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
                <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-brand-500 to-indigo-500 scale-x-0 group-hover:scale-x-100 origin-left smooth-transition" />
              </button>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-sm text-gray-500 py-12">No roles match that filter.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default Careers;
